import { ClickAllMessage } from "../contentScript/initContentScript";
import { logError, logInfo } from "./log";
import { normalizeUrl } from "./normalizeUrl";

/**
 * Determines if the current iframe matches any of the rules in the message
 * Returns true if at least one rule matches the iframe
 */
export const isMatchedIframe = (message: ClickAllMessage): boolean => {
  // Safety check - are we in an iframe?
  if (window.self === window.top) {
    logInfo("Not in an iframe, no matching needed");
    return true; // Not an iframe, so no matching needed
  }

  // Get the iframe element
  const iframe = window.frameElement as HTMLIFrameElement;
  if (!iframe) {
    logInfo("Cannot access frameElement, might be cross-origin");
    return false;
  }

  // If there are no clicks or message is invalid, no match
  if (!message.clicks || message.clicks.length === 0) {
    logInfo("No clicks in message, cannot match iframe");
    return false;
  }

  // Check if any rule matches this iframe
  return message.clicks.some((click) => {
    // Check if iframe URL matches
    if (click.iframeUrl && iframe.src) {
      logInfo("Comparing iframe URLs:", {
        iframeSrc: iframe.src,
        clickIframeUrl: click.iframeUrl,
      });

      // Match by domain comparison
      if (normalizeUrl(iframe.src) === normalizeUrl(click.iframeUrl)) {
        logInfo("✅ Iframe URL domain match");
        return true;
      }

      // Extract base URL without query parameters
      try {
        const iframeUrlObj = new URL(iframe.src);
        const clickUrlObj = new URL(click.iframeUrl);

        // If hostnames and pathname match, it's likely the same iframe with different params
        if (
          iframeUrlObj.hostname === clickUrlObj.hostname &&
          iframeUrlObj.pathname === clickUrlObj.pathname
        ) {
          logInfo("✅ Iframe base URL match (same host and path)");
          return true;
        }
      } catch (e) {
        logError("Error comparing URLs:", e);
      }

      // Also check for exact match or contains
      if (
        iframe.src === click.iframeUrl ||
        iframe.src.includes(click.iframeUrl) ||
        click.iframeUrl.includes(iframe.src)
      ) {
        logInfo("✅ Iframe URL direct match");
        return true;
      }
    }

    // Check if iframe name matches - support partial matching for IDs that follow patterns
    if (click.iframeName && (iframe.name || iframe.id)) {
      const iframeName = iframe.name || iframe.id || "";
      logInfo("Comparing iframe names/IDs:", {
        iframeName: iframeName,
        clickIframeName: click.iframeName,
      });

      // Exact match
      if (iframeName === click.iframeName) {
        logInfo("✅ Iframe name/ID exact match");
        return true;
      }

      // Handle pattern like sp_message_iframe_XXXXXX where XXXXXX is a changing ID
      // Extract the pattern part without the number
      const patternMatch = /^(.*?)_?\d+$/.exec(click.iframeName);
      if (patternMatch && patternMatch[1]) {
        const basePattern = patternMatch[1];
        if (iframeName.startsWith(basePattern)) {
          logInfo(`✅ Iframe name/ID pattern match: ${basePattern}`);
          return true;
        }
      }
    }

    // No match for this rule
    return false;
  });
};
